import { useState, useEffect } from 'react';
import { FaUpload, FaTrash, FaImage, FaSpinner, FaPlus, FaVolumeUp, FaEye, FaTimes, FaCamera, FaCode, FaList } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { extractTextFromImage, generateSentencesFromText } from '../../../utilities/extensions/imageToText';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const OppositesActivity = ({ activityId, initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    words: [{ 
      word: '', 
      wordEnglishMeaning: '',
      opposite: '', 
      oppositeEnglishMeaning: '',
      wordAudio: null,
      oppositeAudio: null,
      wordEnglishAudio: null,
      oppositeEnglishAudio: null,
      image: null,
      confusingLetters: []
    }]
  });
  const [activeTab, setActiveTab] = useState('form'); // 'form' or 'json'
  const [jsonInput, setJsonInput] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState({});
  
  // Image-to-text popup state
  const [showImageToTextPopup, setShowImageToTextPopup] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [extractedText, setExtractedText] = useState(''); // eslint-disable-line no-unused-vars
  const [generatedWords, setGeneratedWords] = useState([]);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [translating, setTranslating] = useState(false);

  // Build storage path using the helper
  const buildStoragePath = ({
    wordId,
    fileName
  }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;
    
    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'opposites',
      fileName,
      itemId: wordId
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        
        // Auto-migrate legacy image fields if they exist
        if (parsed.words) {
          parsed.words = parsed.words.map(word => {
            if (!word.image && (word.wordImage || word.oppositeImage)) {
              return {
                ...word,
                image: word.wordImage || word.oppositeImage
              };
            }
            return word;
          });
        }
        
        setContent(parsed);
        setJsonInput(JSON.stringify(parsed, null, 2));
      } catch {
        // Keep default content if parsing fails
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
    setJsonInput(JSON.stringify(content, null, 2));
  }, [content, onContentChange]);

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonInput(value);
    try {
      const parsed = JSON.parse(value);
      setContent(parsed);
    } catch {
      // Invalid JSON, don't update content yet
    }
  };

  const addWord = () => {
    const newWords = [...content.words, { 
      word: '', 
      wordEnglishMeaning: '',
      opposite: '', 
      oppositeEnglishMeaning: '',
      wordAudio: null,
      oppositeAudio: null,
      wordEnglishAudio: null,
      oppositeEnglishAudio: null,
      image: null,
      confusingLetters: []
    }];
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const removeWord = async (index) => {
    if (content.words.length > 1) {
      const wordToDelete = content.words[index];
      const storage = getStorage();
      
      // Delete all associated files from Firebase Storage
      const fileTypes = ['wordAudio', 'oppositeAudio', 'wordEnglishAudio', 'oppositeEnglishAudio', 'image'];
      
      for (const fileType of fileTypes) {
        if (wordToDelete[fileType]) {
          try {
            const fileRef = ref(storage, wordToDelete[fileType]);
            await deleteObject(fileRef);
          } catch (error) {

        // Ignore errors
          }
        }
      }
      
      const newWords = content.words.filter((_, i) => i !== index);
      setContent(prev => ({ ...prev, words: newWords }));
    }
  };

  const updateWord = (index, field, value) => {
    const newWords = [...content.words];
    newWords[index] = { ...newWords[index], [field]: value };
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const uploadFile = async (file, index, fileType) => {
    if (!file || !chapterId || !grade) {
      alert('Missing required information for file upload');
      return;
    }

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const wordId = `word${index + 1}`;
      
      const storagePath = buildStoragePath({
        wordId,
        fileName: file.name
      });

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      
      updateWord(index, fileType, storagePath);
      
    } catch (error) {

      alert(`Failed to upload file: ${error.message}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeFile = async (index, fileType) => {
    const word = content.words[index];
    if (word[fileType]) {
      try {
        const storage = getStorage();
        const fileRef = ref(storage, word[fileType]);
        await deleteObject(fileRef);
        updateWord(index, fileType, null);
      } catch (error) {

        alert(`Failed to delete file: ${error.message}`);
      }
    }
  };

  const playAudio = async (audioPath) => {
    if (audioPath) {
      try {
        const storage = getStorage();
        const audioRef = ref(storage, audioPath);
        const downloadURL = await getDownloadURL(audioRef);
        const audio = new Audio(downloadURL);
        audio.play().catch(() => {});
      } catch (error) {

        // Ignore errors
      }
    }
  };

  // Image-to-text functions
  const handleImageUploadForText = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedImage(file);
    
    // Create blob URL for preview
    const blobUrl = URL.createObjectURL(file);
    setImageBlobUrl(blobUrl);
    
    setImageProcessing(true);
    setExtractedText('');
    setGeneratedWords([]);

    try {
      // Extract text from image
      const storage = getStorage();
      const text = await extractTextFromImage(file, storage, db);
      setExtractedText(text);
      
      // Generate word pairs from extracted text
      const sentences = generateSentencesFromText(text);
      
      // Convert sentences to opposites format
      const words = sentences.map(sentence => {
        const languageText = sentence.languageText || '';
        let word = '';
        let opposite = '';
        
        // Split by hyphen if it exists (word - opposite format)
        if (languageText.includes('-')) {
          const parts = languageText.split('-');
          word = parts[0].trim();
          opposite = parts.slice(1).join('-').trim();
        } else {
          word = languageText;
        }
        
        return {
          word,
          wordEnglishMeaning: '',
          opposite,
          oppositeEnglishMeaning: '',
          wordAudio: null,
          oppositeAudio: null,
          wordEnglishAudio: null,
          oppositeEnglishAudio: null,
          image: null,
          confusingLetters: []
        };
      });
      
      setGeneratedWords(words);
    } catch (error) {

      alert('Failed to process image: ' + error.message);
    } finally {
      setImageProcessing(false);
    }
  };

  const addGeneratedWords = () => {
    if (generatedWords.length > 0) {
      const newWords = [...content.words, ...generatedWords];
      setContent({ ...content, words: newWords });
      closeImageToTextPopup();
    }
  };

  const closeImageToTextPopup = () => {
    setShowImageToTextPopup(false);
    setUploadedImage(null);
    if (imageBlobUrl) {
      URL.revokeObjectURL(imageBlobUrl);
      setImageBlobUrl(null);
    }
    setExtractedText('');
    setGeneratedWords([]);
    setImageProcessing(false);
  };

  const editGeneratedWord = (index, field, value) => {
    const updated = [...generatedWords];
    updated[index] = { ...updated[index], [field]: value };
    setGeneratedWords(updated);
  };

  const removeGeneratedWord = (index) => {
    const updated = generatedWords.filter((_, i) => i !== index);
    setGeneratedWords(updated);
  };

  const addRowAbove = (index) => {
    const newWord = {
      word: '',
      wordEnglishMeaning: '',
      opposite: '',
      oppositeEnglishMeaning: '',
      wordAudio: null,
      oppositeAudio: null,
      wordEnglishAudio: null,
      oppositeEnglishAudio: null,
      image: null,
      confusingLetters: []
    };
    const updated = [...generatedWords];
    updated.splice(index, 0, newWord);
    setGeneratedWords(updated);
  };

  // Generate audio for existing word pairs (content.words) that are missing audio
  const generateAudioForExistingWords = async () => {
    if (!content.words || content.words.length === 0) {
      alert('No word pairs available for audio generation');
      return;
    }

    const ttsRequests = [];

    content.words.forEach((wordPair, index) => {
      // Word audio missing
      if (
        wordPair.word &&
        wordPair.word.trim() &&
        !wordPair.wordAudio
      ) {
        ttsRequests.push({
          text: wordPair.word,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          wordIndex: index,
          type: 'word'
        });
      }

      // Opposite audio missing
      if (
        wordPair.opposite &&
        wordPair.opposite.trim() &&
        !wordPair.oppositeAudio
      ) {
        ttsRequests.push({
          text: wordPair.opposite,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          wordIndex: index,
          type: 'opposite'
        });
      }

      // Word English audio missing
      if (
        wordPair.wordEnglishMeaning &&
        wordPair.wordEnglishMeaning.trim() &&
        !wordPair.wordEnglishAudio
      ) {
        ttsRequests.push({
          text: wordPair.wordEnglishMeaning,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          wordIndex: index,
          type: 'wordEnglish'
        });
      }

      // Opposite English audio missing
      if (
        wordPair.oppositeEnglishMeaning &&
        wordPair.oppositeEnglishMeaning.trim() &&
        !wordPair.oppositeEnglishAudio
      ) {
        ttsRequests.push({
          text: wordPair.oppositeEnglishMeaning,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          wordIndex: index,
          type: 'oppositeEnglish'
        });
      }
    });

    if (ttsRequests.length === 0) {
      alert('All word pairs already have audio for the available texts');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();

      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map((req) => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender,
        })),
        db,
        storage
      );

      const updatedWords = [...content.words];

      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (!audioUrl || audioUrl.startsWith('[TTS Error:')) return;

        const { wordIndex, type } = request;
        if (type === 'word') {
          updatedWords[wordIndex].wordAudio = audioUrl;
        } else if (type === 'opposite') {
          updatedWords[wordIndex].oppositeAudio = audioUrl;
        } else if (type === 'wordEnglish') {
          updatedWords[wordIndex].wordEnglishAudio = audioUrl;
        } else if (type === 'oppositeEnglish') {
          updatedWords[wordIndex].oppositeEnglishAudio = audioUrl;
        }
      });

      setContent((prev) => ({ ...prev, words: updatedWords }));
    } catch (error) {

      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Generate audio for generated words (only for fields that don't have audio yet)
  const generateAudioForWords = async () => {
    if (generatedWords.length === 0) return;

    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const ttsRequests = [];
      
      generatedWords.forEach((wordPair, index) => {
        // Add word audio request only if audio is missing
        if (wordPair.word && wordPair.word.trim() && !wordPair.wordAudio) {
          ttsRequests.push({
            text: wordPair.word,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            type: 'word'
          });
        }
        
        // Add opposite audio request only if audio is missing
        if (wordPair.opposite && wordPair.opposite.trim() && !wordPair.oppositeAudio) {
          ttsRequests.push({
            text: wordPair.opposite,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            type: 'opposite'
          });
        }
        
        // Add word English meaning audio request only if audio is missing
        if (wordPair.wordEnglishMeaning && wordPair.wordEnglishMeaning.trim() && !wordPair.wordEnglishAudio) {
          ttsRequests.push({
            text: wordPair.wordEnglishMeaning,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            type: 'wordEnglish'
          });
        }
        
        // Add opposite English meaning audio request only if audio is missing
        if (wordPair.oppositeEnglishMeaning && wordPair.oppositeEnglishMeaning.trim() && !wordPair.oppositeEnglishAudio) {
          ttsRequests.push({
            text: wordPair.oppositeEnglishMeaning,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            type: 'oppositeEnglish'
          });
        }
      });

      if (ttsRequests.length === 0) {
        setGeneratingAudio(false);
        return;
      }

      // Generate audio for all requests
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );
      
      // Map audio URLs back to words
      const updatedWords = [...generatedWords];
      
      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          const { wordIndex, type } = request;
          if (type === 'word') {
            updatedWords[wordIndex].wordAudio = audioUrl;
          } else if (type === 'opposite') {
            updatedWords[wordIndex].oppositeAudio = audioUrl;
          } else if (type === 'wordEnglish') {
            updatedWords[wordIndex].wordEnglishAudio = audioUrl;
          } else if (type === 'oppositeEnglish') {
            updatedWords[wordIndex].oppositeEnglishAudio = audioUrl;
          }
        }
      });
      
      setGeneratedWords(updatedWords);
    } catch (error) {

      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Translate words to English for meanings
  const translateWords = async () => {
    if (generatedWords.length === 0) return;

    setTranslating(true);

    try {
      // Extract words that don't have English meanings
      const wordsToTranslate = [];
      const oppositesToTranslate = [];
      
      generatedWords.forEach((wordPair, index) => {
        if (wordPair.word && wordPair.word.trim() && !wordPair.wordEnglishMeaning) {
          wordsToTranslate.push({ text: wordPair.word, index, type: 'word' });
        }
        if (wordPair.opposite && wordPair.opposite.trim() && !wordPair.oppositeEnglishMeaning) {
          oppositesToTranslate.push({ text: wordPair.opposite, index, type: 'opposite' });
        }
      });

      const allToTranslate = [...wordsToTranslate, ...oppositesToTranslate];
      
      if (allToTranslate.length === 0) {
        alert('All words already have English meanings or no words to translate.');
        return;
      }

      // Translate the words (Kannada to English)
      const textsToTranslate = allToTranslate.map(item => item.text);
      const translatedTexts = await translateBatchToText(
        textsToTranslate, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.KANNADA, 
        Constants.LANGUAGES.ENGLISH
      );
      
      // Update words with English meanings
      const updatedWords = [...generatedWords];
      allToTranslate.forEach((item, translationIndex) => {
        const translatedText = translatedTexts[translationIndex];
        if (translatedText && !translatedText.startsWith('[Translation Error:')) {
          if (item.type === 'word') {
            updatedWords[item.index].wordEnglishMeaning = translatedText;
          } else if (item.type === 'opposite') {
            updatedWords[item.index].oppositeEnglishMeaning = translatedText;
          }
        }
      });
      
      setGeneratedWords(updatedWords);
    } catch (error) {

      alert('Failed to translate words: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  // Generate images for word pairs that are missing them
  const generateImagesForExistingWords = async () => {
    if (!content.words || content.words.length === 0) {
      alert('No word pairs available for image generation');
      return;
    }

    // Check if there are any missing images
    const hasMissingImages = content.words.some(w => !w.image);
    if (!hasMissingImages) {
      alert('All word pairs already have images');
      return;
    }

    setGeneratingImages(true);

    try {
      const functions = getFunctions();
      const generateImagesFn = httpsCallable(functions, 'generateWordMeaningImages');
      
      // We need to pass the current content so the cloud function knows what to generate for
      await generateImagesFn({
        activityId,
        baseContent: JSON.stringify(content),
        chapterId,
        language: (selectedChapter?.subject || 'language').toLowerCase(),
        grade,
        activityType: 'OPPOSITES',
      });

      alert('Image generation started. Please refresh the page in a few moments to see the images.');
    } catch (error) {
      console.error('Failed to generate images:', error);
      alert('Failed to generate images: ' + error.message);
    } finally {
      setGeneratingImages(false);
    }
  };

  const validate = () => {
    const errors = [];
    
    if (!content.words || content.words.length === 0) {
      errors.push('At least one word pair is required');
    }

    content.words?.forEach((wordPair, index) => {
      if (!wordPair.word?.trim()) {
        errors.push(`Word is required for pair ${index + 1}`);
      }
      if (!wordPair.wordEnglishMeaning?.trim()) {
        errors.push(`Word English meaning is required for pair ${index + 1}`);
      }
      if (!wordPair.opposite?.trim()) {
        errors.push(`Opposite word is required for pair ${index + 1}`);
      }
      if (!wordPair.oppositeEnglishMeaning?.trim()) {
        errors.push(`Opposite English meaning is required for pair ${index + 1}`);
      }
    });

    return errors;
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          className={`px-6 py-3 font-medium text-sm flex items-center gap-2 transition-colors ${
            activeTab === 'form'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
          onClick={() => setActiveTab('form')}
        >
          <FaList className="w-4 h-4" />
          Form Editor
        </button>
        <button
          className={`px-6 py-3 font-medium text-sm flex items-center gap-2 transition-colors ${
            activeTab === 'json'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
          onClick={() => setActiveTab('json')}
        >
          <FaCode className="w-4 h-4" />
          JSON Editor
        </button>
      </div>

      {activeTab === 'json' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Direct JSON Edit</h3>
            <span className="text-xs text-gray-500">Paste your JSON content here for bulk updates</span>
          </div>
          <textarea
            className="w-full h-[500px] p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            value={jsonInput}
            onChange={handleJsonChange}
            placeholder="Paste valid JSON here..."
          />
          {(() => {
            try {
              JSON.parse(jsonInput);
              return <p className="mt-2 text-xs text-green-600">✓ Valid JSON</p>;
            } catch (e) {
              return <p className="mt-2 text-xs text-red-600">✗ Invalid JSON: {e.message}</p>;
            }
          })()}
        </div>
      ) : (
        <>
          {/* Word Pairs Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              🔄 <span className="ml-2">Opposites Word Pairs</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">{content.words.length} pair{content.words.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm"
              onClick={() => setShowImageToTextPopup(true)}
            >
              <FaCamera className="w-4 h-4 mr-2" />
              Extract from Image
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              onClick={addWord}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Word Pair
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm disabled:bg-purple-300 disabled:cursor-not-allowed text-sm"
              onClick={generateImagesForExistingWords}
              disabled={generatingImages || !content.words || content.words.length === 0}
            >
              {generatingImages ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating Images...
                </>
              ) : (
                <>
                  <FaImage className="w-4 h-4 mr-2" />
                  Generate Missing Images
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-sm disabled:bg-orange-300 disabled:cursor-not-allowed text-sm"
              onClick={generateAudioForExistingWords}
              disabled={generatingAudio || !content.words || content.words.length === 0}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating Audio...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Missing Audio
                </>
              )}
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {content.words.map((word, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-4">
                {/* Index */}
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {index + 1}
                </div>
                
                {/* Text Fields */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Word *"
                    value={word.word || ''}
                    onChange={(e) => updateWord(index, 'word', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Word English *"
                    value={word.wordEnglishMeaning || ''}
                    onChange={(e) => updateWord(index, 'wordEnglishMeaning', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Opposite *"
                    value={word.opposite || ''}
                    onChange={(e) => updateWord(index, 'opposite', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Opposite English *"
                    value={word.oppositeEnglishMeaning || ''}
                    onChange={(e) => updateWord(index, 'oppositeEnglishMeaning', e.target.value)}
                  />
                </div>
                  
                {/* Audio Files - Compact */}
                <div className="flex items-center gap-2">
                  {/* Word Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`w-audio-${index}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) uploadFile(file, index, 'wordAudio');
                      }}
                      disabled={uploadingFiles[`${index}-wordAudio`]}
                    />
                    <label
                      htmlFor={`w-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="Word Audio"
                    >
                      🎵 W
                    </label>
                    {uploadingFiles[`${index}-wordAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.wordAudio && !uploadingFiles[`${index}-wordAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.wordAudio)}
                          title="Play Word Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'wordAudio')}
                          title="Remove Word Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Opposite Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`o-audio-${index}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) uploadFile(file, index, 'oppositeAudio');
                      }}
                      disabled={uploadingFiles[`${index}-oppositeAudio`]}
                    />
                    <label
                      htmlFor={`o-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="Opposite Audio"
                    >
                      🎵 O
                    </label>
                    {uploadingFiles[`${index}-oppositeAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.oppositeAudio && !uploadingFiles[`${index}-oppositeAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.oppositeAudio)}
                          title="Play Opposite Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'oppositeAudio')}
                          title="Remove Opposite Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Word English Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`we-audio-${index}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) uploadFile(file, index, 'wordEnglishAudio');
                      }}
                      disabled={uploadingFiles[`${index}-wordEnglishAudio`]}
                    />
                    <label
                      htmlFor={`we-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-green-50 text-green-700 rounded text-xs hover:bg-green-100 transition-colors"
                      title="Word English Audio"
                    >
                      🎵 WE
                    </label>
                    {uploadingFiles[`${index}-wordEnglishAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-green-600" />}
                    {word.wordEnglishAudio && !uploadingFiles[`${index}-wordEnglishAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.wordEnglishAudio)}
                          title="Play Word English Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'wordEnglishAudio')}
                          title="Remove Word English Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Opposite English Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`oe-audio-${index}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) uploadFile(file, index, 'oppositeEnglishAudio');
                      }}
                      disabled={uploadingFiles[`${index}-oppositeEnglishAudio`]}
                    />
                    <label
                      htmlFor={`oe-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-green-50 text-green-700 rounded text-xs hover:bg-green-100 transition-colors"
                      title="Opposite English Audio"
                    >
                      🎵 OE
                    </label>
                    {uploadingFiles[`${index}-oppositeEnglishAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-green-600" />}
                    {word.oppositeEnglishAudio && !uploadingFiles[`${index}-oppositeEnglishAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.oppositeEnglishAudio)}
                          title="Play Opposite English Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'oppositeEnglishAudio')}
                          title="Remove Opposite English Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Image - Compact */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`comparison-image-${index}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) uploadFile(file, index, 'image');
                      }}
                      disabled={uploadingFiles[`${index}-image`]}
                    />
                    <label
                      htmlFor={`comparison-image-${index}`}
                      className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                      title="Comparison Image"
                    >
                      🖼️ Image
                    </label>
                    {uploadingFiles[`${index}-image`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                    {word.image && !uploadingFiles[`${index}-image`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          onClick={async () => {
                            try {
                              const storage = getStorage();
                              const imageRef = ref(storage, word.image);
                              const downloadURL = await getDownloadURL(imageRef);
                              window.open(downloadURL, '_blank');
                            } catch (error) {
                              alert(`Failed to preview image. Error: ${error.message}`);
                            }
                          }}
                          title="Preview Comparison Image"
                        >
                          <FaEye className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'image')}
                          title="Remove Comparison Image"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Confusing Letters - Compact */}
                <input
                  type="text"
                  className="w-32 px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Confusing letters"
                  value={word.confusingLetters?.join(' ') || ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    const letters = input.split(/[\s,]+/).filter(letter => letter.trim() !== '');
                    updateWord(index, 'confusingLetters', letters);
                  }}
                  title={`${word.confusingLetters?.length || 0} letters • Separate with spaces`}
                />
                
                {/* Remove Button */}
                {content.words.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removeWord(index)}
                    title="Remove Word Pair"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image-to-Text Popup */}
      {showImageToTextPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Extract Opposite Pairs from Image</h3>
                <p className="text-sm text-gray-600 mt-1">Upload an image to automatically extract opposite word pairs</p>
              </div>
              <button
                onClick={closeImageToTextPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {!uploadedImage ? (
                /* Upload Section */
                <div className="text-center">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-emerald-400 transition-colors">
                    <div className="flex flex-col items-center">
                      <FaCamera className="w-12 h-12 text-gray-400 mb-4" />
                      <label htmlFor="imageToTextUpload" className="text-lg font-medium text-gray-900 mb-2 cursor-pointer">
                        Upload an image to extract opposite pairs
                      </label>
                      <input
                        type="file"
                        id="imageToTextUpload"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUploadForText}
                        disabled={imageProcessing}
                      />
                      <button
                        type="button"
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:bg-emerald-400 disabled:cursor-not-allowed mb-3"
                        onClick={() => document.getElementById('imageToTextUpload').click()}
                        disabled={imageProcessing}
                      >
                        Choose Image File
                      </button>
                      <p className="text-sm text-gray-500">
                        Supported formats: JPG, PNG, GIF. Use format: &quot;word - opposite&quot; for best results.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Processing and Results Section */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Uploaded Image */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FaImage className="text-blue-600" />
                        Uploaded Image
                      </h4>
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <img 
                          src={imageBlobUrl} 
                          alt="Uploaded" 
                          className="w-full h-auto rounded-lg shadow-sm"
                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                        />
                      </div>
                      <button 
                        className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300 transition-colors flex items-center justify-center gap-2 text-sm"
                        onClick={() => {
                          closeImageToTextPopup();
                        }}
                      >
                        <FaUpload className="w-4 h-4" />
                        Upload Different Image
                      </button>
                    </div>
                  </div>
                  
                  {/* Generated Words */}
                  <div className="lg:col-span-2">
                    {imageProcessing ? (
                      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <div className="flex flex-col items-center gap-4">
                          <FaSpinner className="w-12 h-12 animate-spin text-emerald-500" />
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">Processing Image</h4>
                            <p className="text-gray-600 text-sm">Analyzing image and extracting opposite pairs using AI...</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 mb-4">
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
                            <div>
                              <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                ✨ Generated opposite pairs ({generatedWords.length})
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button 
                                className="bg-purple-500 hover:bg-purple-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                                onClick={translateWords}
                                disabled={translating || generatingAudio || generatedWords.length === 0}
                              >
                                {translating ? (
                                  <>
                                    <FaSpinner className="w-4 h-4 animate-spin" />
                                    Translating...
                                  </>
                                ) : (
                                  <>
                                    🌐 Translate Words
                                  </>
                                )}
                              </button>
                              <button 
                                className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                                onClick={generateAudioForWords}
                                disabled={generatingAudio || translating || generatedWords.length === 0}
                              >
                                {generatingAudio ? (
                                  <>
                                    <FaSpinner className="w-4 h-4 animate-spin" />
                                    Generating Audio...
                                  </>
                                ) : (
                                  <>
                                    🔊 Generate Audio
                                  </>
                                )}
                              </button>
                              <button 
                                className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-green-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                                onClick={addGeneratedWords}
                                disabled={generatedWords.length === 0}
                              >
                                <FaPlus className="w-4 h-4" />
                                Add All Pairs
                              </button>
                            </div>
                          </div>
                        </div>

                        {generatedWords.length > 0 ? (
                          <div className="space-y-4 max-h-96 overflow-y-auto">
                          {generatedWords.map((wordPair, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Word</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={wordPair.word}
                                    onChange={(e) => editGeneratedWord(index, 'word', e.target.value)}
                                    placeholder="Language word..."
                                  />
                                  {wordPair.wordAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(wordPair.wordAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Word English</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={wordPair.wordEnglishMeaning}
                                    onChange={(e) => editGeneratedWord(index, 'wordEnglishMeaning', e.target.value)}
                                    placeholder="English meaning..."
                                  />
                                  {wordPair.wordEnglishAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(wordPair.wordEnglishAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Opposite</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={wordPair.opposite}
                                    onChange={(e) => editGeneratedWord(index, 'opposite', e.target.value)}
                                    placeholder="Opposite word..."
                                  />
                                  {wordPair.oppositeAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(wordPair.oppositeAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Opposite English</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={wordPair.oppositeEnglishMeaning}
                                    onChange={(e) => editGeneratedWord(index, 'oppositeEnglishMeaning', e.target.value)}
                                    placeholder="Opposite English..."
                                  />
                                  {wordPair.oppositeEnglishAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(wordPair.oppositeEnglishAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-end gap-1">
                                <button 
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                  onClick={() => addRowAbove(index)}
                                  title="Add row above this pair"
                                >
                                  <FaPlus className="w-4 h-4" />
                                </button>
                                <button 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  onClick={() => removeGeneratedWord(index)}
                                  title="Remove this pair"
                                >
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        ) : (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <span className="text-4xl">📝</span>
                              <div>
                                <h4 className="text-lg font-semibold text-blue-900 mb-1">No pairs generated</h4>
                                <p className="text-blue-700 text-sm">The image processing didn&apos;t generate pairs. Try uploading a different image with clearer text in &quot;word - opposite&quot; format.</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Validation Display */}
      {(() => {
        const errors = validate();
        if (errors.length > 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-6 h-6 text-amber-600 mr-3 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start text-amber-700">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}
      </>
      )}
    </div>
  );
};

// Export validation function separately for use in parent component
export const validateOppositesContent = (content) => {
  const errors = [];
  
  if (!content.words || content.words.length === 0) {
    errors.push('At least one word pair is required');
  }

  content.words?.forEach((wordPair, index) => {
    if (!wordPair.word?.trim()) {
      errors.push(`Word is required for pair ${index + 1}`);
    }
    if (!wordPair.wordEnglishMeaning?.trim()) {
      errors.push(`Word English meaning is required for pair ${index + 1}`);
    }
    if (!wordPair.opposite?.trim()) {
      errors.push(`Opposite word is required for pair ${index + 1}`);
    }
    if (!wordPair.oppositeEnglishMeaning?.trim()) {
      errors.push(`Opposite English meaning is required for pair ${index + 1}`);
    }
    
    // Validate English meanings: must start with capital, should not end with period
    if (wordPair.wordEnglishMeaning?.trim()) {
      const trimmed = wordPair.wordEnglishMeaning.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`Word English meaning for pair ${index + 1} must start with a capital letter`);
      }
      if (/\.$/.test(trimmed)) {
        errors.push(`Word English meaning for pair ${index + 1} should not end with a period`);
      }
    }
    if (wordPair.oppositeEnglishMeaning?.trim()) {
      const trimmed = wordPair.oppositeEnglishMeaning.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`Opposite English meaning for pair ${index + 1} must start with a capital letter`);
      }
      if (/\.$/.test(trimmed)) {
        errors.push(`Opposite English meaning for pair ${index + 1} should not end with a period`);
      }
    }
  });

  return errors;
};

export default OppositesActivity;
